// projectsStore.ts
import {
  createTestRequirementAPI,
  deleteTestProjectAPI,
  deleteTestRequirementAPI,
  getProjectsAPI,
  getTestPlanContentAPI,
  getTestPlansAPI,
  getTestProjectByNodeIdAPI,
  getTestSpecificationContentAPI,
  updateTestRequirementAPI,
} from "Zustand/services/projectsService";
import { ProjectsState } from "Zustand/types/projectsTypes";
import { StateCreator } from "zustand";
import {
  createAttachmentAPI,
  updateAttachmentAPI,
  uploadFilesAPI,
  uploadFilesAPIByFileId,
  uploadFilesWithProgress,
} from "Zustand/services/attachmentService";
import { namespace } from "Zustand/constants";
import { v4 as uuidv4 } from "uuid";
import {
  createTestReportAPI,
  getTestReportByNodeIdAPI,
  getTestReportsByPlanAPI,
  updateTestRequirementReportAPI,
} from "Zustand/services/reportServices";

export const createProjectsSlice: StateCreator<
  ProjectsState,
  [],
  [],
  ProjectsState
> = (set, get) => ({
  loading: false,
  creatingTestReport: false,
  error: null,

  project: null,

  projects: [],
  testPlans: [],
  selectedTestPlanContent: [],
  isUploading: false,

  testReports: [],
  testReportsLoading: false,
  testReportsError: null,
  currentReport: null,
  currentReportLoading: false,

  setIsUploading: (value: boolean) => set({ isUploading: value }),

  getProjects: async (legalentityNodeId: string) => {
    set({ loading: true, error: null });
    try {
      const projects = await getProjectsAPI(legalentityNodeId);
      set({ projects, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  getTestPlans: async (projectId: string) => {
    set({ loading: true, error: null });
    try {
      const testPlans = await getTestPlansAPI(projectId);
      set({ testPlans, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  getTestPlanContent: async (planId: string) => {
    set({ loading: true, error: null });
    try {
      const content = await getTestPlanContentAPI(planId);
      set({ selectedTestPlanContent: content, loading: false });
    } catch (err: any) {
      set({ selectedTestPlanContent: [], error: err.message, loading: false });
    }
  },

  getTestSpecificationContent: async (testSpecificationId: string) => {
    set({ loading: true, error: null });
    try {
      const content = await getTestSpecificationContentAPI(testSpecificationId);
      set({ selectedTestPlanContent: content, loading: false });
    } catch (err: any) {
      set({ selectedTestPlanContent: [], error: err.message, loading: false });
    }
  },

  createTestRequirement: async (payload: string) => {
    set({ loading: true, error: null });
    try {
      await createTestRequirementAPI(payload);
      set({ loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  updateTestRequirement: async (payload: string) => {
    set({ loading: true, error: null });
    try {
      await updateTestRequirementAPI(payload);
      set({ loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  deleteTestRequirement: async (nodeid: string) => {
    try {
      await deleteTestRequirementAPI(nodeid);
      // Optionally remove from local state too
    } catch (err: any) {
      console.error("Delete failed", err);
      throw err;
    }
  },

  uploadAttachments: async (
    nodeid: string, // this stays for grouping attachments together if needed
    files: any[],
    toEdit: any[]
  ) => {
    try {
      // STEP 1: Handle edits
      for (const editItem of toEdit) {
        await updateAttachmentAPI(editItem);
      }

      if (files.length === 0) return;

      // STEP 2: Create unique nodeids for each file
      const nodeIdMapping = files.map(() => {
        const uuid = uuidv4();
        const nodeid = namespace + "/" + uuid;
        return { uuid, nodeid };
      });

      const fileInputs = files.map((file: any, i: number) => ({
        nodeid: nodeIdMapping[i].nodeid,
        filename: file.file.name,
      }));

      const response = await uploadFilesAPI(fileInputs);

      await uploadFilesWithProgress(response, files);

      const uploaded = response.map((res: any, i: number) => ({
        fileid: res.fileid,
        bucketid: res.bucketid,
        fileversion: res.fileversion,
        mimetype: files[i].file.type,
        file: files[i],
        nodeid: nodeIdMapping[i].nodeid,
        identifier: nodeIdMapping[i].uuid,
      }));

      const payloadForAttachmentAPI: any = uploaded.map((item) => ({
        identifier: item.identifier,
        lang: "en",
        title: item.file.title,
        description: item.file.description,
        specifier: item.file.specifier ?? item.file.file?.specifier ?? "",
        isattachmentofnodeid: nodeid,
        file: {
          fileid: item.fileid,
          bucketid: item.bucketid,
          fileversion: item.fileversion,
          mimetype: item.mimetype,
        },
      }));

      for (const item of payloadForAttachmentAPI) {
        await createAttachmentAPI(item);
      }
    } catch (err) {
      console.error("Error uploading attachments:", err);
      throw err;
    }
  },

  // Overwrite existing files in place (e.g. a cropped remote image): request
  // upload links by fileid and PUT the new bytes. The attachment record keeps
  // pointing to the same fileid, so nothing else needs updating.
  replaceAttachmentFiles: async (files: any[]) => {
    try {
      if (files.length === 0) return;

      const response = await uploadFilesAPIByFileId(
        files.map((file: any) => ({ nodeid: file.nodeid, fileid: file.fileid }))
      );

      await uploadFilesWithProgress(response, files);
    } catch (err) {
      console.error("Error replacing attachment files:", err);
      throw err;
    }
  },

  getProjectByNodeId: async (nodeid) => {
    try {
      const data = await getTestProjectByNodeIdAPI(nodeid);
      // backend usually wraps it: { testproject: { … } }
      set({ project: data });
    } catch (err) {
      console.error("Fetch project failed", err);
      throw err;
    }
  },

  deleteProject: async (nodeid) => {
    try {
      await deleteTestProjectAPI(nodeid);
    } catch (err) {
      console.error("Delete project failed", err);
      throw err;
    }
  },

  getTestReports: async (testPlanNodeId: string) => {
    set({ testReportsLoading: true, testReportsError: null });
    try {
      const data = await getTestReportsByPlanAPI(testPlanNodeId);

      set({ testReports: data.testplan.hastestreport ?? [], testReportsLoading: false });
    } catch (e: any) {
      set({
        testReportsLoading: false,
        testReportsError: e?.message ?? "Failed to load reports",
      });
    }
  },

  async getTestReportByNodeId(nodeid: string) {
    set({ currentReportLoading: true });
    try {
      const data: any = await getTestReportByNodeIdAPI(nodeid);
      set({ currentReport: data ?? null, currentReportLoading: false });
    } catch {
      set({ currentReport: null, currentReportLoading: false });
    }
  },

  async updateTestRequirementReports(payload: any) {
    // payload: only changed fields + required ids
    await updateTestRequirementReportAPI(payload);
    // optional: refresh list/detail if you want
    const cr: any = get().currentReport;
    if (cr?.nodeid === payload?.nodeid) {
      // shallow merge locally
      set({ currentReport: { ...cr, ...payload } });
    }
  },

  async createTestReport(testPlanNodeId: string) {
    set({ creatingTestReport: true });
    try {
      const data = await createTestReportAPI(testPlanNodeId);
      // optional: refresh reports list, e.g.
      // await get().getTestReports(testPlanNodeId);
      return data;
    } catch (err) {
      console.error("createTestReport failed:", err);
      throw err;
    } finally {
      set({ creatingTestReport: false });
    }
  },

  async resetReportsState() {
    set({
      testReports: [],
      testReportsLoading: false,
      testReportsError: null,
      currentReport: null,
      currentReportLoading: false,
    });
  }

});
